/**
 * postMessage protocol between the admin ReviewView (parent) and the frontend
 * pages loaded in its iframes (frames). Versioned so either side can be
 * deployed first; unknown/mismatched messages are ignored.
 */
export const BRIDGE_VERSION = 1 as const;
/** Query param that activates the bridge on a frontend page. */
export const REVIEW_PARAM = '_promoteReview';
/** Env var (both sites): comma-separated parent origins allowed to drive the bridge. */
export const PARENT_ORIGINS_ENV = 'NEXT_PUBLIC_PROMOTE_PARENT_ORIGINS';

export interface BlockAnchor {
  blockId: string;
  top: number;
  height: number;
}

export type ParentToFrame =
  | { v: typeof BRIDGE_VERSION; type: 'hello' }
  | { v: typeof BRIDGE_VERSION; type: 'get-anchors' }
  | { v: typeof BRIDGE_VERSION; type: 'scroll-to'; blockId?: string; top?: number }
  | { v: typeof BRIDGE_VERSION; type: 'highlight'; blockId: string; tone: 'add' | 'del' }
  | { v: typeof BRIDGE_VERSION; type: 'clear-highlights' };

export type FrameToParent =
  | { v: typeof BRIDGE_VERSION; type: 'ready' }
  | { v: typeof BRIDGE_VERSION; type: 'anchors'; anchors: BlockAnchor[]; docHeight: number }
  | { v: typeof BRIDGE_VERSION; type: 'scrolled'; top: number; height: number };

const isObj = (d: unknown): d is Record<string, unknown> => typeof d === 'object' && d !== null;

export function isParentToFrame(d: unknown): d is ParentToFrame {
  if (!isObj(d) || d.v !== BRIDGE_VERSION) return false;
  switch (d.type) {
    case 'hello':
    case 'get-anchors':
    case 'clear-highlights':
      return true;
    case 'scroll-to':
      return d.blockId === undefined || typeof d.blockId === 'string'
        ? d.top === undefined || typeof d.top === 'number'
        : false;
    case 'highlight':
      return typeof d.blockId === 'string' && (d.tone === 'add' || d.tone === 'del');
    default:
      return false;
  }
}

export function isFrameToParent(d: unknown): d is FrameToParent {
  if (!isObj(d) || d.v !== BRIDGE_VERSION) return false;
  switch (d.type) {
    case 'ready':
      return true;
    case 'anchors':
      return Array.isArray(d.anchors) && typeof d.docHeight === 'number';
    case 'scrolled':
      return typeof d.top === 'number' && typeof d.height === 'number';
    default:
      return false;
  }
}

export function parseAllowedOrigins(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function isOriginAllowed(origin: string, allowed: string[]): boolean {
  return allowed.includes(origin);
}
